import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Avatar({ p, size = "h-24 w-24", text = "text-xl" }) {
  const initials = p.nama.replace(/^(H\.|Hj\.|Ust\.|Ustdzh\.)\s*/g, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (p.photo) {
    return <img src={imgUrl(p.photo)} alt={p.nama} className={`${size} rounded-full object-cover border-2 border-[#D4AF37]`} />;
  }
  return (
    <span className={`${size} rounded-full bg-emerald-800 text-white ${text} font-bold flex items-center justify-center border-2 border-[#D4AF37]`}>
      {initials}
    </span>
  );
}

export default function ProfilPage() {
  const { t, lang } = useLang();
  const [profile, setProfile] = useState(null);
  const [pengurus, setPengurus] = useState([]);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/pengurus").then((r) => setPengurus(r.data)).catch(() => {});
  }, []);

  const content = profile ? (lang === "en" && profile.content_en ? profile.content_en : profile.content_id) : "";
  const ketua = pengurus[0];
  const anggota = pengurus.slice(1);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="profil-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-emerald-950 tracking-tight">{t("profil.title")}</h1>
      </motion.div>

      <Tabs defaultValue="tentang" className="mt-10">
        <TabsList>
          <TabsTrigger value="tentang" data-testid="profil-tab-tentang">{t("profil.tab_about")}</TabsTrigger>
          <TabsTrigger value="pengurus" data-testid="profil-tab-pengurus">{t("profil.tab_pengurus")}</TabsTrigger>
        </TabsList>

        <TabsContent value="tentang" className="mt-8">
          <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8 sm:p-12 max-w-4xl" data-testid="profil-content">
            <div className="prose-content text-slate-700 text-base leading-relaxed">
              {content.split("\n\n").filter(Boolean).map((par, i) => (
                <p key={i}>{par}</p>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pengurus" className="mt-8">
          <p className="text-sm text-slate-600 mb-10">{t("profil.pengurus_sub")}</p>
          {ketua && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center mb-12" data-testid={`pengurus-card-${ketua.id}`}>
              <Avatar p={ketua} size="h-32 w-32" text="text-3xl" />
              <h3 className="font-display text-2xl font-bold text-emerald-900 mt-4">{ketua.nama}</h3>
              <span className="mt-1.5 text-xs font-semibold uppercase tracking-widest text-emerald-950 bg-[#D4AF37]/30 border border-[#D4AF37] rounded-full px-4 py-1">{ketua.jabatan}</span>
            </motion.div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {anggota.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i }}
                className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6 flex flex-col items-center text-center"
                data-testid={`pengurus-card-${p.id}`}
              >
                <Avatar p={p} />
                <h4 className="text-sm font-bold text-emerald-950 mt-4">{p.nama}</h4>
                <p className="text-xs text-slate-500 mt-1">{p.jabatan}</p>
              </motion.div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
